<script lang="ts" setup>
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledInput from "@/components/StyledInput.vue";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { ContactType } from "@/interfaces/contactType";
import { Hotel } from "@/internal/modules/providers/interfaces/Hotel";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, ref } from "vue";
import ContactEntry from "../components/ContactEntry.vue";
import useHotelContacts from "../composables/useHotelContacts";
import useHotelVerifyCredit from "../composables/useHotelVerifyCredit";

interface Props {
  hotel: Hotel;
}

const props = defineProps<Props>();

const { hasPermissionUser } = useHasPermissionUser();

const {
  hotelContacts,
  isLoading: isLoadingContacts,
  createHotelContactAsync,
  createHotelContactLoading,
  page,
  refetch,
} = useHotelContacts({ 
  immediate: true,
  params: {
    id: props.hotel.id,
  },
  config: {
    errorHandling: false,
  },
});

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= hotelContacts.value?.meta.lastPage!) {
    page.value = newPage;
    refetch();
  }
};

const openModal = ref(false);

const handleAddContact = () => {
  openModal.value = !openModal.value;
};

const q = ref("");

interface Form {
  contactType: ContactType[] | undefined;
  name: string;
  jobTitle: string;
  phone?: string;
}

const form = reactive<Form>({
  contactType: [],
  name: "",
  jobTitle: "",
  phone: "",
});

const queryClient = useQueryClient();

const eliminateContactType = (id: number) => {
  form.contactType = form.contactType!.filter(
    (contactType) => contactType.id !== id,
  );
};

const handleCreate = async () => {
  await createHotelContactAsync({
    hotelId: props.hotel.id,
    name: form.name,
    jobTitle: form.jobTitle,
    contactTypeIds: form.contactType?.map((contactType) => contactType.id)!,
  });

  await queryClient.invalidateQueries(["hotel-contacts"]);

  handleAddContact();
  form.contactType = [];
  form.name = "";
  form.jobTitle = "";
};

const { filteredContactsType } = useHotelVerifyCredit(props.hotel.id);
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-2xl font-bold">Contactos</h1>

      <StyledButton
        v-if="
          hasPermissionUser('create-contacts-internal', 'App\\Models\\Hotel') &&
          hasPermissionUser('viewAny-internal', 'App\\Models\\ContactType')
        "
        class="bg-blue-500 hover:bg-blue-900 text-white"
        @click="handleAddContact"
      >
        Agregar contacto
      </StyledButton>
    </div>

    <ContactEntry
      v-for="contact in hotelContacts?.data"
      :key="contact.id"
      :contact="contact"
    />

    <div class="flex w-full justify-end">
      <PaginateNav
        v-if="hotelContacts?.data.length"
        :page="page"
        :pagination="hotelContacts"
        :data="hotelContacts?.data || []"
        :handle-page="handlePage"
      />
    </div>
  </ShadowBox>

  <!-- Modal create contact -->
  <ModalComponent
    :model-value="openModal"
    @update:model-value="
      () => {
        handleAddContact();
      }
    "
  >
    <template #modalBody>
      <ShadowBox class="bg-white">
        <h1 class="text-2xl font-bold">Agregar contacto</h1>

        <form
          action=""
          @submit.prevent="handleCreate()"
          class="flex flex-col gap-3"
        >
          <div class="grid grid-cols-3 gap-2">
            <div
              v-for="contact in form.contactType"
              :key="contact.id"
              class="bg-blue-300 rounded-md p-1.5 hover:bg-blue-500 transition-all cursor-pointer"
            >
              <div class="flex items-center justify-between gap-2">
                <p class="text-white text-sm">
                  {{ contact.name }}
                </p>
                <button
                  @click="eliminateContactType(contact.id)"
                  class="text-red-500 hover:text-red-900 text-sm font-bold"
                >
                  X
                </button>
              </div>
            </div>
          </div>

          <StyledCombobox
            label="Tipo de contactos"
            multiple
            v-model="form.contactType"
            :options="filteredContactsType || []"
            @input-change="($event) => (q = $event)"
          >
            <template #default="{ option }">
              <span>{{ option.name }}</span>
            </template>
          </StyledCombobox>

          <StyledInput
            v-model="form.name"
            label="Nombre"
            placeholder="Nombre"
            :is-required="true"
          />

          <StyledInput
            v-model="form.jobTitle"
            label="Puesto"
            placeholder="Nombre del puesto"
          />

          <StyledButton
            class="bg-blue-500 hover:bg-blue-900 ml-auto mt-2 text-white"
            :disabled="createHotelContactLoading"
            :loading="createHotelContactLoading"
          >
            Guardar
          </StyledButton>
        </form>
      </ShadowBox>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoadingContacts" />
</template>
